import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  Grid,
  Stack,
  Button,
  Box,
  Alert,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import CharCountField from '../../components/seo/CharCountField';
import SerpPreview from '../../components/seo/SerpPreview';
import SocialCardPreview from '../../components/seo/SocialCardPreview';
import { uploadViaBackend } from '../../utils/uploadImage';
import { getBlogPost, saveBlogSeo } from './seoApi';

// SEO editor for a single blog post, reached from the SEO dashboard's Blog
// Posts section. Edits only the SEO slice (slug, publish flag, meta overrides);
// the article content itself is edited in Blog Management. Unlike static pages
// there is no draft stage — saving applies immediately to the published post.
export default function SeoBlogEditor() {
  const [params] = useSearchParams();
  const id = params.get('id') || '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [post, setPost] = useState(null);
  const [values, setValues] = useState({
    slug: '',
    published: false,
    seoTitle: '',
    seoDescription: '',
    seoOgImage: '',
    seoNoindex: false,
  });

  useEffect(() => {
    (async () => {
      const res = await getBlogPost(id);
      if (res?.status === 200 && res.data?._id) {
        const p = res.data;
        setPost(p);
        setValues({
          slug: p.slug || '',
          published: !!p.published,
          seoTitle: p.seo?.title || '',
          seoDescription: p.seo?.description || '',
          seoOgImage: p.seo?.ogImage || '',
          seoNoindex: !!p.seo?.noindex,
        });
      } else {
        toast.error(res?.data?.message || 'Failed to load blog post');
      }
      setLoading(false);
    })();
  }, [id]);

  const set = (name) => (e) => setValues((v) => ({ ...v, [name]: e.target.value }));

  const handleOgUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        setUploading(true);
        const url = await uploadViaBackend(file);
        setValues((v) => ({ ...v, seoOgImage: url }));
        toast.success('Image uploaded');
      } catch (e) {
        toast.error('Image upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const doSave = async () => {
    setSaving(true);
    const res = await saveBlogSeo(post._id, values);
    setSaving(false);
    if (res?.status === 200) {
      const updated = res.data.updatedPost;
      toast.success('SEO settings saved');
      if (post.slug && updated.slug !== post.slug && post.published) {
        toast.info(`Old link /blog/${post.slug} now redirects to the new URL.`);
      }
      setPost(updated);
      setValues((v) => ({ ...v, slug: updated.slug || v.slug }));
    } else {
      toast.error(res?.data?.message || 'Failed to save');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Blog post not found.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/dashboard/seo')}>Back to SEO dashboard</Button>
      </Container>
    );
  }

  // Mirror the server-side fallbacks so previews match what actually renders.
  const previewTitle = values.seoTitle || `${post.title} | Jet Home Loans Blog`;
  const previewDesc = values.seoDescription || post.previewBlurb || '';
  const previewImage = values.seoOgImage || post.imageUrl || '';
  const previewPath = `/blog/${values.slug || post._id}`;

  return (
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" sx={{ maxWidth: 560, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {post.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">{previewPath}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={values.published ? 'Published' : 'Draft'}
            color={values.published ? 'success' : 'default'}
          />
          <Button
            variant="text"
            onClick={() => navigate(`/dashboard/seo/history?path=${encodeURIComponent(`/blog/${post.slug || post._id}`)}`)}
          >
            History
          </Button>
          <Button variant="text" onClick={() => navigate(`/dashboard/blog/${post.slug || post._id}`)}>
            Edit article content
          </Button>
          <Button variant="outlined" onClick={() => navigate('/dashboard/seo')}>Back</Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Blog SEO changes apply immediately when saved — there is no separate draft step.
      </Alert>

      <Grid container spacing={3}>
        {/* Editor */}
        <Grid item xs={12} md={7}>
          <Card sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>URL slug</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={values.slug}
                  onChange={set('slug')}
                  helperText="Changing a published post's URL automatically redirects the old link, so nothing breaks."
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!values.published}
                    onChange={(e) => setValues((v) => ({ ...v, published: e.target.checked }))}
                  />
                }
                label="Published (visible on the public site and in the sitemap)"
              />

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">SEARCH RESULT</Typography>
              </Divider>

              <CharCountField
                label="SEO title"
                value={values.seoTitle}
                onChange={set('seoTitle')}
                recommended={60}
                max={120}
                placeholder={`${post.title} | Jet Home Loans Blog`}
                helperText="Falls back to the post title if left blank."
              />
              <CharCountField
                label="Meta description"
                value={values.seoDescription}
                onChange={set('seoDescription')}
                recommended={155}
                max={320}
                multiline
                rows={3}
                placeholder={post.previewBlurb}
                helperText="Falls back to the preview blurb if left blank."
              />

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">SOCIAL SHARE (Facebook / LinkedIn)</Typography>
              </Divider>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Social share image</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="outlined" onClick={handleOgUpload} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Upload image'}
                  </Button>
                  {values.seoOgImage && (
                    <Button size="small" color="error" onClick={() => setValues((v) => ({ ...v, seoOgImage: '' }))}>
                      Use cover image instead
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Falls back to the post's cover image. Recommended 1200×630.
                </Typography>
              </Box>

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">ADVANCED</Typography>
              </Divider>

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!values.seoNoindex}
                      onChange={(e) => setValues((v) => ({ ...v, seoNoindex: e.target.checked }))}
                    />
                  }
                  label="Hide this post from Google (noindex)"
                />
                {values.seoNoindex && (
                  <Alert severity="warning" sx={{ mt: 0.5 }}>
                    This will remove the post from Google search results.
                  </Alert>
                )}
              </Box>

              <Stack direction="row" spacing={2} pt={1}>
                <Button variant="contained" onClick={doSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Live previews */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Google result preview</Typography>
              <SerpPreview path={previewPath} title={previewTitle} description={previewDesc} />
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Social share preview</Typography>
              <SocialCardPreview
                path={previewPath}
                title={previewTitle}
                description={previewDesc}
                ogImage={previewImage}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}
